import { PropsWithChildren, ReactNode, useContext } from "react"
import {
  Dialog,
  ModalOverlay,
  ModalOverlayProps,
  OverlayTriggerStateContext,
  Modal as RACModal,
} from "react-aria-components"
import { useTranslation } from "react-i18next"
import { OverlayTriggerState } from "react-stately"

import { IconButton } from "@/globalComponents/atoms/Button"
import { cn } from "@/utilities/etc"

import styles from "./Modal.module.css"

export interface ModalProps extends ModalOverlayProps {
  modalStyle?: "centered" | "drawer" // Default is centered
}

const Modal = (props: PropsWithChildren<ModalProps>) => {
  return (
    <ModalOverlay className={styles.overlay} {...props}>
      <RACModal
        className={
          props.modalStyle === "drawer"
            ? styles.drawerModal
            : styles.centeredModal
        }
      >
        <Dialog className={styles.dialog}>{props.children}</Dialog>
      </RACModal>
    </ModalOverlay>
  )
}

interface ModalHeaderProps {
  hideClose?: boolean
  isCloseDisabled?: boolean
  className?: string
}

Modal.Header = (props: PropsWithChildren<ModalHeaderProps>) => {
  const context = useContext(OverlayTriggerStateContext)
  const { t } = useTranslation()
  return (
    <div
      className={cn(styles.modalHeader, props.className)}
      data-closable={!props.hideClose ? "true" : "false"}
    >
      {props.children}
      {!props.hideClose && (
        <IconButton
          buttonType="default"
          presentation="ghost"
          aria-label={t("common.close")}
          onPress={context?.close}
          iconName="close"
          isDisabled={props.isCloseDisabled}
        />
      )}
    </div>
  )
}

Modal.Body = (props: PropsWithChildren<{ className?: string }>) => {
  return (
    <div className={cn(styles.modalBody, props.className)}>
      {props.children}
    </div>
  )
}

interface ModalFooterProps {
  arrangement?: "vertical" | "horizontal"
  children: (opts: OverlayTriggerState) => ReactNode
}

Modal.Footer = (props: ModalFooterProps) => {
  const context = useContext(OverlayTriggerStateContext)
  if (!context) {
    console.error("Modal.Footer must be used in a Modal")
    return <></>
  }
  return (
    <div
      className={styles.modalFooter}
      data-arrangement={props.arrangement || "horizontal"}
    >
      {props.children(context)}
    </div>
  )
}

export default Modal
